import { useEffect, useRef, useState } from 'react';

/**
 * useAiPrefill — handle AI-Assistant-triggered prefill of a tool page.
 *
 * When the AI Assistant routes to a page (Compress/Convert/SplitExtract/…)
 * with a file in `location.state`, this hook:
 *   1. Calls `onPrefill(state)` once so the page can do its own setup
 *      (setSearchParams, setLevel, setImgFormat, setAiGroupsRaw, etc.).
 *   2. Calls `loadFn(state.file)` to load the file.
 *   3. Flips `aiPrefilled` to true so the page can show an AI-prefill banner.
 *
 * The effect is keyed on the file reference and guarded by a ref so it fires
 * exactly once per fresh File. Each AI execute produces a new File, so a new
 * reference unlocks the prefill.
 *
 * We deliberately avoid the alternative of stripping AI keys via a
 * replace-navigate — that mutates `location.state`, re-fires this effect
 * mid-mount, and ends up calling `loadFn` twice (the second call destroys
 * the just-loaded pdfDoc and re-parses). Same bug shape as Viewer 8fb1e5b /
 * Compress 27bc6c0 / Convert e759354 / SplitExtract add11a7.
 *
 * @param {object} params
 * @param {object} params.location  React Router location (from useLocation()).
 * @param {(file: File) => void} params.loadFn  Page's existing file loader.
 * @param {(state: object) => void} [params.onPrefill]  Optional per-page setup
 *   run with location.state before loadFn fires.
 * @returns {{ aiPrefilled: boolean }}
 */
export function useAiPrefill({ location, loadFn, onPrefill }) {
  const [aiPrefilled, setAiPrefilled] = useState(false);
  const prefillFileRef = useRef(null);

  useEffect(() => {
    const stateFile = location.state?.file;
    if (!stateFile) return;
    if (prefillFileRef.current === stateFile) return;
    prefillFileRef.current = stateFile;
    if (onPrefill) onPrefill(location.state);
    loadFn(stateFile);
    setAiPrefilled(Object.keys(location.state || {}).some((key) => key.startsWith('ai')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.file]);

  return { aiPrefilled };
}
